import React, { useState, useEffect } from 'react';
import { Department } from '@/pages/Departments';
import DepartmentCombobox from './DepartmentCombobox';
import { CollaboratorCombobox } from '../../shared';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';

interface DepartmentFormProps {
    department?: Department | null;
    departments: Department[];
    onSave: (departmentData: Omit<Department, 'id' | 'completeDescription' | 'children'>, parent: Department | null) => void;
    onCancel: () => void;
    isSubmitting: boolean;
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({ department, departments, onSave, onCancel, isSubmitting }) => {
    const [name, setName] = useState('');
    const [parentId, setParentId] = useState<string | null>(null);
    const [responsible, setResponsible] = useState<Collaborator | null>(null);
    const { data: collaborators = [] } = useCollaboratorsRealtimeQuery();

    useEffect(() => {
        if (department) {
            setName(department.name);
            setParentId(department.parentId);
            if (collaborators.length > 0) {
                setResponsible(collaborators.find((c: Collaborator) => c.id === department.responsibleId) || null);
            }
        } else {
            setName('');
            setParentId(null);
            setResponsible(null);
        }
    }, [department, collaborators]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name) {
            alert('O nome do departamento é obrigatório.');
            return;
        }

        const parent = parentId ? departments.find(d => d.id === parentId) || null : null;
        onSave({
            name,
            parentId,
            responsibleId: responsible?.id,
            responsibleName: responsible?.name
        }, parent);
    };

    return (
        <form onSubmit={handleSubmit} className="p-4 bg-white shadow-md rounded-lg space-y-4">
            <h2 className="text-lg font-semibold">{department ? 'Editar Departamento' : 'Novo Departamento'}</h2>
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nome</label>
                <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-primary w-full mt-1"
                    required
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="parent" className="block text-sm font-medium text-gray-700">Departamento Pai</label>
                    <DepartmentCombobox
                        departments={departments}
                        selectedId={parentId}
                        onChange={(parent) => setParentId(parent ? parent.id : null)}
                        disabled={isSubmitting}
                        currentDepartmentId={department?.id}
                    />
                </div>
                <div>
                    <label htmlFor="responsible" className="block text-sm font-medium text-gray-700">Responsável</label>
                    <CollaboratorCombobox
                        selectedId={responsible?.id || null}
                        onChange={setResponsible}
                        disabled={isSubmitting}
                    />
                </div>
            </div>
            <div className="flex justify-end space-x-3">
                <button type="button" onClick={onCancel} className="btn-secondary" disabled={isSubmitting}>
                    Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
            </div>
        </form>
    );
};

export default DepartmentForm; 